import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { getAllDocumentNames } from '../../documents/store';
import { getMLTestDocumentsUploadModalOpen, setInitialUploadMLTestDocuments, toggleMLTestDocumentsUploadModal, getMLTestFilesToUpload, getIsUploadingMLTestDocument, updateMLTestDocumentName, uploadMLTestDocumentStarted, getFileDocumentNameIds } from './store';
import { UploadMLTestDocuments } from './UploadMLTestDocumentsModal';

export const AIPlaygroundDocumentUploadModal: React.FC = () => {
    const dispatch = useAppDispatch();

    const isOpen = useAppSelector(getMLTestDocumentsUploadModalOpen);
    const isUploading = useAppSelector(getIsUploadingMLTestDocument);
    const files = useAppSelector(getMLTestFilesToUpload);
    const documentNames = useAppSelector(getAllDocumentNames);
    const selectedDocuments = useAppSelector(getFileDocumentNameIds);

    const documentNamesOptions: DropdownOption[] = useMemo(() => documentNames.length > 0 ? documentNames.map(({ documentNameId, documentName }) => ({ value: documentNameId!.toString(), label: documentName })) : [], [documentNames]);
    const selectedDocumentsWithoutName = useMemo(() => selectedDocuments.filter(({ documentNameId }) => isNull(documentNameId)).length > 0, [selectedDocuments]);

    const getDocumentNameValue = useCallback((filename: string): DropdownOption | null => {
        const documentNameId = selectedDocuments.find(({ documentDescription }) => documentDescription === filename)?.documentNameId;
        if (documentNameId) {
            return documentNamesOptions.find(instance => instance.value === documentNameId.toString()) || null;
        }
        return null;
    }, [selectedDocuments, documentNamesOptions]);

    const setDocumentsForUpload = useCallback((files: File[]) => dispatch(setInitialUploadMLTestDocuments(files)), [dispatch]);
    const closeModal = useCallback(() => dispatch(toggleMLTestDocumentsUploadModal(false)), [dispatch]);

    const updateDropdownValue = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null, documentDescription: string) => {
        let value = null;
        if (!isNull(dropdownValue)) {
            value = parseInt((dropdownValue as DropdownOption).value);
        }
        dispatch(updateMLTestDocumentName(value, documentDescription));
    }, [dispatch]);

    const uploadDocument = useCallback(() => dispatch(uploadMLTestDocumentStarted()), [dispatch]);

    return (
        <UploadMLTestDocuments
            isOpen={isOpen}
            setUploadDocuments={setDocumentsForUpload}
            modalLabel='Upload New ML Test Document(s)'
            files={files}
            isUploading={isUploading}
            uploadDocument={uploadDocument}
            closeModal={closeModal}
            getDocumentNameValue={getDocumentNameValue}
            documentNames={documentNamesOptions}
            updateDropdownValue={updateDropdownValue}
            disabled={selectedDocumentsWithoutName}
        />
    );
};
